import { useMemo } from 'react';
import {
  useStoreCategories,
  useStorefrontCustomProductCollectionPickerOptions,
  type WebsiteInterfaceLocale,
} from '@dukanti/shared';
import { useProducts } from '@/hooks/use-products';
import { supabase } from '@/lib/supabase';
import type { NativeSelectOption } from '../components/native-select';

export type ReferenceKind = 'category' | 'product' | 'custom-collection';

export interface ReferenceOptions {
  readonly options: readonly NativeSelectOption[];
  readonly isLoading: boolean;
  readonly isError: boolean;
}

/**
 * What a merchant can point a link or a reference at: the categories, the
 * products and the custom collections of the store, from the modules that
 * already own them. The editor never manages them, it only lists them.
 * Only the query of the wanted kind runs.
 */
export function useReferenceOptions(
  kind: ReferenceKind | null,
  storeId: string,
  locale: WebsiteInterfaceLocale,
): ReferenceOptions {
  const categories = useStoreCategories(supabase, kind === 'category' ? storeId : null);
  const products = useProducts(kind === 'product' ? storeId : null);
  const collections = useStorefrontCustomProductCollectionPickerOptions(supabase, {
    storeId: kind === 'custom-collection' ? storeId : null,
    search: '',
  });

  return useMemo<ReferenceOptions>(() => {
    if (kind === 'category') {
      return {
        options: categories.categories.map((category) => ({
          value: category.id,
          label: category.name,
        })),
        isLoading: categories.isLoading,
        isError: categories.isError,
      };
    }
    if (kind === 'product') {
      return {
        options: (products.data ?? []).map((product) => ({
          value: product.id,
          label: product.name,
        })),
        isLoading: products.isLoading,
        isError: products.isError,
      };
    }
    if (kind === 'custom-collection') {
      return {
        options: (collections.data ?? [])
          .filter((collection) => collection.isSelectable)
          .map((collection) => ({
            value: collection.collectionRef,
            label: collection.name[locale] || collection.name.en || collection.collectionRef,
          })),
        isLoading: collections.isLoading,
        isError: collections.isError,
      };
    }
    return { options: [], isLoading: false, isError: false };
  }, [kind, locale, categories, products, collections]);
}
